import cn from 'classnames';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import TableOfContentsGroup, { Heading } from './group';
import styles from './styles.module.scss';

const DEFAULT_TOC_HEADINGS = 'h2, h3, h4, h5, h6';
const DEFAULT_TOC_SELECTOR = '.editor-styles-wrapper';

export interface TableOfContentsProps {
  querySelector: string;
  includeHeadings: string;
  className?: string;
}

const TableOfContents: React.FC<TableOfContentsProps> = ({ querySelector, includeHeadings, className, ...rest }) => {
  const [headings, setHeadings] = useState<Heading[]>([]);

  const [active, setActive] = useState<string | null>(null);
  const [inViewport, setInViewport] = useState<string | null>(null);

  const generateHeadingsTree = useCallback(() => {
    const stack: Heading[] = [{ title: 'root', level: 'H0', children: [] }];
    const generatedHeadingsIds: string[] = [];
    let dom: HTMLElement;
    try {
      dom = document.querySelector(querySelector ?? DEFAULT_TOC_SELECTOR) as HTMLElement;
    } catch {
      return [];
    }
    if (!dom) {
      return [];
    }
    const countIdOccurrence = (element?: string) => {
      if (!element) {
        return 0;
      }
      let counter = 0;
      for (const item of generatedHeadingsIds.flat()) {
        if (item == element) {
          counter++;
        }
      }
      return counter;
    };
    const kebabCase = (s: string | null) =>
      s
        ? s
            .replace(/([a-z])([A-Z])/g, '$1-$2')
            .replace(/[^a-zA-Z0-9\s_-]/g, '')
            .replace(/[\s_]+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-+|-+$/g, '')
            .toLowerCase()
        : undefined;
    let selectedHeadings: NodeListOf<HTMLElement>;
    try {
      selectedHeadings = dom.querySelectorAll(includeHeadings ?? DEFAULT_TOC_HEADINGS);
    } catch {
      return [];
    }
    if (!selectedHeadings) {
      return [];
    }
    for (const header of Array.from(selectedHeadings)) {
      const { tagName: level, textContent: title, id: headerId } = header;
      let id = headerId || kebabCase(title);
      if (id) {
        const idCount = countIdOccurrence(id);
        generatedHeadingsIds.push(id);
        if (idCount > 0) {
          id = `${id}-${idCount}`;
        }
        header.setAttribute('id', id);
      }
      const node = { level: level, title, id, children: [] };
      let last = stack[stack.length - 1];
      while (typeof last !== 'undefined' && last.level >= node.level) {
        stack.pop();
        last = stack[stack.length - 1];
      }
      if (last) {
        last.children = last.children || [];
        last.children.push(node);
        stack.push(node);
      }
    }
    return stack[0].children;
  }, [includeHeadings, querySelector]);

  useEffect(() => {
    const hash = location.hash.replace('#', '');

    if (hash && !!hash.length) {
      setActive(hash);
    }
  }, []);

  useEffect(() => {
    // Collect all heading IDs from the tree in order
    const collectIds = (items: Heading[]): string[] => {
      return items.reduce((acc, item) => {
        if (item.id) acc.push(item.id);
        if (item.children.length > 0) acc.push(...collectIds(item.children));
        return acc;
      }, [] as string[]);
    };

    const allIds = collectIds(headings);
    const elements = allIds.map(id => document.getElementById(id)).filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    // Track visible elements with their positions
    const visibleElements = new Map<string, number>();

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const id = entry.target.id;

          if (entry.isIntersecting) {
            visibleElements.set(id, entry.boundingClientRect.top);
          } else {
            visibleElements.delete(id);
          }
        });

        // Find the topmost visible element
        if (visibleElements.size > 0) {
          let topmostId = null;
          let topmostPosition = Infinity;

          visibleElements.forEach((top, id) => {
            if (top < topmostPosition) {
              topmostPosition = top;
              topmostId = id;
            }
          });

          if (topmostId) {
            setInViewport(topmostId);
          }
        }
        // If nothing is visible, keep the last in-viewport element (don't update state)
      },
      {
        rootMargin: '0px 0px -50% 0px',
        threshold: 0
      }
    );

    elements.forEach(el => observer.observe(el));

    return () => {
      observer.disconnect();
    };
  }, [headings]);

  useEffect(() => setHeadings(generateHeadingsTree()), [generateHeadingsTree]);

  if (!headings.length) return null;

  return (
    <div className={cn(styles.container, className)}>
      <div {...rest}>
        <TableOfContentsGroup headings={headings} active={active} inViewport={inViewport} />
      </div>
    </div>
  );
};

export default TableOfContents;
